import { Component, Input, OnDestroy } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, firstValueFrom, Subject, take, takeUntil } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Provider, ProviderWarehouse, Warehouse } from 'src/typings';
import { AppState } from 'src/app/redux/app.state';
import { WarehouseService } from 'src/app/warehouse/warehouse.service';
import { ProviderWarehouseCreateComponent } from '../provider-warehouse-create/create.component';
import { summarizeDelivery } from '../provider-warehouse-delivery.util';
import { ProviderWarehouseService } from '../provider-warehouse.service';
import { deliveryForReceiver, initialReceiverWarehouseId } from '../provider-warehouse-route.util';

@Component({
  selector: 'app-provider-warehouses-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss']
})
export class ProviderWarhouseListComponent implements OnDestroy {
  control = new UntypedFormControl();

  warehouses: ProviderWarehouse[] = [];

  receiverWarehouses: Warehouse[] = [];
  receiverWarehouseId: number | null = null;
  receiverLoadError = '';
  loading = false;
  error = '';
  deletingId: number | null = null;
  dialogOpen = false;
  private searchRevision = 0;
  private destroyed = false;
  private destroy$ = new Subject<void>();

  get receiverQueryParams() {
    return { receiverWarehouseId: this.receiverWarehouseId };
  }

  columnsToDisplay = ['id', 'wh', 'city', 'delivery', 'menu'];

  @Input() set provider(payload: Provider | undefined) {
    if (!payload) return;
    this._provider = payload;
    this.search();
  }

  private _provider: Provider | undefined;

  get provider() {
    return this._provider;
  }

  constructor(
    private providerWarehouseService: ProviderWarehouseService,
    private dialog: MatDialog,
    private warehouseService: WarehouseService,
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.control.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => this.search());
    void this.loadReceivers();
  }

  private async loadReceivers() {
    try {
      const [warehouses, user] = await Promise.all([
        firstValueFrom(this.warehouseService.getAllList().pipe(takeUntil(this.destroy$))),
        firstValueFrom(
          this.store
            .select((state) => state.user.info)
            .pipe(filter(Boolean), take(1), takeUntil(this.destroy$))
        )
      ]);
      if (this.destroyed) return;
      this.receiverWarehouses = warehouses;
      this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((query) => {
        this.receiverWarehouseId = initialReceiverWarehouseId(
          warehouses,
          query.get('receiverWarehouseId'),
          user?.defaultWarehouse?.id
        );
      });
    } catch {
      if (this.destroyed) return;
      this.receiverLoadError = 'Не удалось загрузить склады получения.';
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectReceiver(id: number) {
    this.receiverWarehouseId = this.receiverWarehouses.some((warehouse) => warehouse.id === id)
      ? id
      : null;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.receiverQueryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  addWh() { this.openWarehouse(); }
  updateWh(wh: ProviderWarehouse) { this.openWarehouse(wh); }
  private openWarehouse(wh?: ProviderWarehouse) {
    if (this.dialogOpen || !this.provider) return;
    this.dialogOpen = true;
    const dialogRef = this.dialog.open(ProviderWarehouseCreateComponent, {
      width: '460px', maxWidth: 'calc(100vw - 24px)', maxHeight: 'calc(100dvh - 24px)'
    });
    dialogRef.componentInstance.provider = this.provider;
    if (wh) dialogRef.componentInstance.wh = wh;
    dialogRef.componentInstance.doneEvent.pipe(take(1), takeUntil(dialogRef.afterClosed()), takeUntil(this.destroy$)).subscribe(() => {
      dialogRef.close(); void this.search();
    });
    dialogRef.afterClosed().pipe(take(1), takeUntil(this.destroy$)).subscribe(() => this.dialogOpen = false);
  }

  async removeWh(wh: ProviderWarehouse) {
    if (this.deletingId !== null || !window.confirm(`Удалить склад «${wh.description || wh.id}»?`)) return;
    const providerId = this.provider?.id;
    this.deletingId = wh.id;
    try {
      await firstValueFrom(this.providerWarehouseService.delete(wh.id).pipe(takeUntil(this.destroy$)));
      if (!this.destroyed && this.provider?.id === providerId) await this.search();
    } catch {
      if (!this.destroyed && this.provider?.id === providerId) this.error = 'Не удалось удалить склад. Проверьте связанные документы.';
    } finally { this.deletingId = null; }
  }

  deliverySummary(wh: ProviderWarehouse) {
    if (!this.receiverWarehouseId) return 'Выберите склад получения';
    return summarizeDelivery(deliveryForReceiver(wh, this.receiverWarehouseId)).join('\n');
  }

  async search() {
    if (!this.provider?.id || this.destroyed) return;
    const revision = ++this.searchRevision;
    this.loading = true; this.error = '';
    try {
      const res = await firstValueFrom(this.providerWarehouseService.search({
        provider_id: this.provider.id, description: this.control.value || undefined
      }).pipe(takeUntil(this.destroy$)));
      if (!this.destroyed && revision === this.searchRevision) this.warehouses = res.data;
    } catch {
      if (!this.destroyed && revision === this.searchRevision) this.error = 'Не удалось загрузить склады поставщика.';
    } finally { if (!this.destroyed && revision === this.searchRevision) this.loading = false; }
  }
}
